import type { Locale } from "./types";

export type TreasuryTab = "offers" | "positions" | "payments";
export type TreasuryPlan = {
  id: string; nominalMicros: string; durationSeconds: number; initialBps: number;
  mentorBps: number; protocolBps: number; available: boolean;
  indicativeGrimNano?: string;
  indicativePriceAt?: string;
};
export type TreasuryPayment = {
  id: string; positionId: string; kind: "initial" | "principal" | "mentor";
  usdtMicros: string; gramNano: string; paidAt: string; transactionUrl?: string;
};
export type TreasuryPosition = {
  id: string; plan: TreasuryPlan; depositedGrimNano: string; initialMicros: string;
  openedAt: string; maturesAt: string; initialPaidAt: string | null; principalPaidAt: string | null;
  walletLabel: string; status: "confirming" | "active" | "settling" | "completed";
};
export type TreasurySnapshot = {
  mode: "preview" | "live"; serverNow: string; revision: number; plans: TreasuryPlan[];
  purchasesEnabled?: boolean;
  rewardAvailableMicros: string; grimAvailableNano: string; walletLabel: string;
  positions: TreasuryPosition[]; payments: TreasuryPayment[];
};
export type TreasuryQuote = {
  id: string; plan: TreasuryPlan; expiresAt: string; grimNano: string;
  rewardDebitMicros: string; initialGramNano: string; networkBudgetNano: string;
  walletLabel: string;
};
type TreasuryWalletMessage = { address: string; amount: string; payload: string };
export type TreasuryWalletRequest = {
  network: "-239"; from: string; validUntil: number;
  messages: [TreasuryWalletMessage] | [TreasuryWalletMessage, TreasuryWalletMessage];
};
export type TreasuryPreparation = { stage: "reserving" | "terms" | "wallet-check" | "closing"; startedAt: number };
export type TreasuryPurchaseStep = (
  | { phase: "reserve" | "arm" | "cancel"; requestId: string; walletRequest: TreasuryWalletRequest }
  | { phase: "deposit"; requestId: string; walletRequest: TreasuryWalletRequest; grimUnits: string; serviceAdvanceNano?: string }
  | { phase: "not-started" | "confirming-reserve" | "reserved" | "confirming-deposit" | "cancelled"; requestId: string }
  | { phase: "expired"; requestId: string; restartable: boolean }
  | { phase: "need-grim"; requestId: string; grimUnits: string }
  | { phase: "position"; requestId: string; ticket: string; rewardsPending: boolean }) & { preparation?: TreasuryPreparation };
/** The page never signs, prices or debits. A live implementation must be server-authoritative. */
export interface TreasuryPort {
  read(): Promise<TreasurySnapshot>;
  quote(planId: string): Promise<TreasuryQuote>;
  open(quoteId: string, requestId: string): Promise<TreasuryPosition>;
  subscribe(listener: (state: TreasurySnapshot) => void): () => void;
  startPurchase?(requestId: string, planId: string): Promise<TreasuryPurchaseStep>;
  purchaseStatus?(requestId: string): Promise<TreasuryPurchaseStep>;
  watchPurchase?(requestId: string, onChange: () => void,
    onState: (state: "live" | "reconnecting") => void): () => void;
  showExamples?(): Promise<void>;
}

export interface TreasuryOverviewPort {
  read(): Promise<unknown>;
  watchOverview?(onChange: () => void, onState: (state: "live" | "reconnecting") => void): () => void;
  startPurchase?(requestId: string, planId: number): Promise<unknown>;
  purchaseStatus?(requestId: string): Promise<unknown>;
  watchPurchase?(requestId: string, onChange: () => void,
    onState: (state: "live" | "reconnecting") => void): () => void;
}

export class TreasuryError extends Error {
  constructor(readonly code: "BALANCE" | "GRIM" | "EXPIRED" | "PLAN" | "UNAVAILABLE" | "STORAGE") { super(code); }
}
export function treasuryBonus(plan: TreasuryPlan): string {
  return (BigInt(plan.nominalMicros) * BigInt(plan.initialBps) / 10_000n).toString();
}
export function treasuryProgress(position: TreasuryPosition, serverNow: number): number {
  const start = Date.parse(position.openedAt), end = Date.parse(position.maturesAt);
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start || !Number.isFinite(serverNow)) return 0;
  return Math.min(1, Math.max(0, (serverNow - start) / (end - start)));
}
/** Exact BigInt formatting: no float conversion of financial amounts. */
export function treasuryUnits(value: string, decimals: number, locale: Locale, precision = 2): string {
  if (!/^\d{1,60}$/.test(value)) return "—";
  const scale = 10n ** BigInt(decimals), amount = BigInt(value);
  const integer = new Intl.NumberFormat(locale === "ru" ? "ru-RU" : "en-US").format(amount / scale);
  const fraction = (amount % scale).toString().padStart(decimals, "0").slice(0, precision).replace(/0+$/, "");
  return `${integer}${fraction ? `${locale === "ru" ? "," : "."}${fraction}` : ""}`;
}
export function treasuryTotals(snapshot: TreasurySnapshot) {
  return {
    activeMicros: snapshot.positions.filter(p => p.status !== "completed").reduce((sum, p) => sum + BigInt(p.plan.nominalMicros), 0n).toString(),
    paidGramNano: snapshot.payments.reduce((sum, p) => sum + BigInt(p.gramNano), 0n).toString(),
    paidUsdtMicros: snapshot.payments.reduce((sum, p) => sum + BigInt(p.usdtMicros), 0n).toString(),
  };
}
export function treasuryExplorerUrl(raw: string | undefined): string | null {
  try { const url = new URL(raw ?? ""); return url.protocol === "https:" && ["tonviewer.com", "tonscan.org"].includes(url.hostname) ? url.href : null; }
  catch { return null; }
}
